import { Button } from "@src/lib/ui/Button";
import {
  useCreateCourseMutation,
  CreatedCourseFragment,
} from "@src/features/marketplace/category-nav/CategorySection.generated";
import { AiChatType } from "@src/types.generated";
import { useRouter } from "next/router";
import { route } from "nextjs-routes";
import NewLibraryItemDialog from "./NewLibraryItemDialog";
import type { AiChatFragment } from "@src/features/assistantChat/aiChat.generated";

export const COURSE_OUTLINE_TITLE = "Create course with AI";
export const COURSE_OUTLINE_DESCRIPTION =
  "Describe your training goals, topics and roles or attach existing materials.\nOpus AI will turn them into a complete editable training course.";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartNewChat: () => void;
  onCourseCreated?: (course: CreatedCourseFragment) => void;
  initialInput?: string;
  enableChatHistory?: boolean;
  onSelectHistoricalChat?: (chat: AiChatFragment) => void;
}

const NewCourseDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onStartNewChat,
  onCourseCreated,
  initialInput,
  enableChatHistory,
  onSelectHistoricalChat,
}) => {
  const router = useRouter();

  const [createCourseMutation] = useCreateCourseMutation({
    variables: {
      name: "",
    },
    onCompleted(data) {
      onOpenChange(false);
      if (data?.createCourse?.course) {
        if (onCourseCreated) {
          onCourseCreated(data.createCourse.course);
        } else {
          window.open(
            route({
              pathname: "/library/library-item/[id]",
              query: { id: data.createCourse.course.libraryItem.id },
            }),
            "_blank",
          );
        }
      }
    },
  });

  const handleBrowseMarketplace = () => {
    onOpenChange(false);
    router.push("/marketplace");
  };

  const FooterButtons = (
    <>
      <Button
        variant="outline"
        className="flex-1 text-primary"
        onClick={() => createCourseMutation()}
      >
        New course from scratch
      </Button>
      <Button
        variant="outline"
        className="flex-1 text-primary"
        onClick={handleBrowseMarketplace}
      >
        Browse marketplace courses
      </Button>
    </>
  );

  return (
    <NewLibraryItemDialog
      open={open}
      onOpenChange={onOpenChange}
      dialogTitle="New Course"
      contentTitle={COURSE_OUTLINE_TITLE}
      contentDescription={COURSE_OUTLINE_DESCRIPTION}
      chatType={AiChatType.CourseOutline}
      FooterButtons={FooterButtons}
      onStartNewChat={onStartNewChat}
      placeholder="Example: Barista latte art basics - cover milk steaming, pouring techniques, and hygiene. Existing training PDF is attached."
      initialInput={initialInput}
      enableChatHistory={enableChatHistory}
      onSelectHistoricalChat={onSelectHistoricalChat}
    />
  );
};

export default NewCourseDialog;
